import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { costingAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line,
} from 'recharts';
import { TrendingDown } from 'lucide-react';

const GARMENT_LABELS = {
  jacket: 'Jacket', trousers: 'Trousers', shirt: 'Shirt', sherwani: 'Sherwani',
  bandhgala: 'Bandhgala', nehru_vest: 'Nehru Vest', kurta: 'Kurta',
  pyjama: 'Pyjama', waistcoat: 'Waistcoat',
};

function getMarginColor(pct) {
  if (pct >= 40) return '#059669';
  if (pct >= 20) return '#D97706';
  return '#DC2626';
}

export default function ProfitabilitySection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await costingAPI.profitability();
        setData(res.data);
      } catch (err) {
        console.error('Failed to fetch profitability', err);
      }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-2 gap-4"><Skeleton className="h-56" /><Skeleton className="h-56" /></div>
    </div>
  );

  if (!data) return null;

  const garmentData = (data.margin_by_garment || []).map(d => ({
    ...d, name: GARMENT_LABELS[d.garment_type] || d.garment_type, fill: getMarginColor(d.avg_margin),
  }));

  const tierData = (data.margin_by_tier || []).map(d => ({
    ...d, name: d.tier, fill: getMarginColor(d.avg_margin),
  }));

  return (
    <div className="space-y-4" data-testid="profitability-section">
      <h2 className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>Profitability</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Avg Margin by Garment */}
        <Card className="rounded-xl border bg-white" data-testid="margin-by-garment-chart">
          <CardHeader className="pb-1 pt-3 px-4">
            <CardTitle className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Avg Margin by Garment Type</CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {garmentData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={garmentData} barCategoryGap="20%">
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} domain={[0, 100]} unit="%" width={35} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }}
                    formatter={(v) => [`${v}%`, 'Avg Margin']} />
                  <Bar dataKey="avg_margin" radius={[4, 4, 0, 0]}>
                    {garmentData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-center py-8" style={{ color: 'var(--ink-muted)' }}>No data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Avg Margin by Fabric Tier */}
        <Card className="rounded-xl border bg-white" data-testid="margin-by-tier-chart">
          <CardHeader className="pb-1 pt-3 px-4">
            <CardTitle className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Avg Margin by Fabric Tier</CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {tierData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={tierData} barCategoryGap="20%">
                  <XAxis dataKey="name" tick={{ fontSize: 8, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} domain={[0, 100]} unit="%" width={35} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }}
                    formatter={(v) => [`${v}%`, 'Avg Margin']} />
                  <Bar dataKey="avg_margin" radius={[4, 4, 0, 0]}>
                    {tierData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-center py-8" style={{ color: 'var(--ink-muted)' }}>No data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Margin Trend */}
        <Card className="rounded-xl border bg-white" data-testid="margin-trend-chart">
          <CardHeader className="pb-1 pt-3 px-4">
            <CardTitle className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Net Margin Trend (6 Months)</CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {(data.monthly_trend || []).some(m => m.order_count > 0) ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data.monthly_trend}>
                  <XAxis dataKey="month_short" tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} unit="%" width={35} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }}
                    formatter={(v) => [`${v}%`, 'Avg Net Margin']} />
                  <Line type="monotone" dataKey="avg_net_margin" stroke="var(--brand-navy)" strokeWidth={2} dot={{ r: 4, fill: 'var(--brand-navy)' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-center py-8" style={{ color: 'var(--ink-muted)' }}>Insufficient data</p>
            )}
          </CardContent>
        </Card>

        {/* Alteration Cost Trend */}
        <Card className="rounded-xl border bg-white" data-testid="alteration-trend-chart">
          <CardHeader className="pb-1 pt-3 px-4">
            <CardTitle className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Alteration Cost % of Order Value</CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {(data.alteration_trend || []).some(m => m.order_count > 0) ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data.alteration_trend}>
                  <XAxis dataKey="month_short" tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} unit="%" width={35} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }}
                    formatter={(v) => [`${v}%`, 'Alteration %']} />
                  <Line type="monotone" dataKey="avg_alteration_pct" stroke="#D97706" strokeWidth={2} dot={{ r: 4, fill: '#D97706' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-center py-8" style={{ color: 'var(--ink-muted)' }}>No alteration data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom 5 Orders */}
      <Card className="rounded-xl border bg-white" data-testid="bottom-5-orders">
        <CardHeader className="pb-1 pt-3 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <TrendingDown className="w-4 h-4" style={{ color: '#DC2626' }} /> Bottom 5 Orders by Margin
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {(data.bottom_5_orders || []).length > 0 ? (
            <div className="space-y-2">
              {data.bottom_5_orders.map((o, idx) => (
                <Link key={o.order_id} to={`/app/orders/${o.order_id}`}
                  className="flex items-center justify-between py-2 px-3 rounded-lg border hover:bg-gray-50/50"
                  style={{ borderColor: 'rgba(27,42,74,0.06)' }}
                  data-testid={`bottom-order-${idx}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold tabular-nums" style={{ color: '#DC2626' }}>#{idx + 1}</span>
                      <span className="text-xs font-semibold" style={{ color: 'var(--brand-navy)' }}>{o.order_number}</span>
                    </div>
                    <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{o.client_name} &middot; {o.garment_summary}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-bold"
                      style={{
                        backgroundColor: getMarginColor(o.net_margin_pct) + '12',
                        color: getMarginColor(o.net_margin_pct),
                      }}>
                      {o.net_margin_pct}%
                    </Badge>
                    <p className="text-[9px] mt-0.5 tabular-nums" style={{ color: 'var(--ink-muted)' }}>
                      ₹{o.net_margin?.toLocaleString('en-IN')} / ₹{o.price_charged?.toLocaleString('en-IN')}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-xs text-center py-6" style={{ color: 'var(--ink-muted)' }}>No order data available</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
